import React from 'react'
import styled from '@emotion/styled'

// Long enough for closing overlays to finish animating out
const DEFERRED_SCROLL_DELAY_MS = 800
// Roughly a second of frames; a late-rendered target needs only one commit
const WAIT_FOR_ELEMENT_MAX_FRAMES = 60

/**
 * Scrolls to the `data-scrollname` target. Returns a cancel function for the
 * pending scroll; callers that can unmount before it lands should call it.
 */
export const scrollToSection = (
  sectionName: string,
  options?: {
    /** Wait a fixed delay, e.g. for a closing overlay's animation */
    deferred?: boolean
    /** Scroll on the first frame the target exists, for late-rendered targets */
    waitForElement?: boolean
    /** Overrides the default ~1s wait budget for targets that mount late */
    maxWaitFrames?: number
  }
): (() => void) => {
  const {
    deferred = false,
    waitForElement = false,
    maxWaitFrames = WAIT_FOR_ELEMENT_MAX_FRAMES
  } = options || {}
  // Quoted attribute value: escape backslashes and quotes; control characters
  // are invalid even inside quotes, so drop them (degrades to a no-match)
  const escaped = sectionName
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, '')
  const find = () => document.querySelector(`[data-scrollname="${escaped}"]`)
  const scroll = () => find()?.scrollIntoView({behavior: 'smooth'})

  if (deferred) {
    // Look the element up at fire time so targets rendered after this call
    // are still found
    const timeoutId = setTimeout(scroll, DEFERRED_SCROLL_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }

  if (waitForElement) {
    let framesLeft = maxWaitFrames
    let frameId: number
    const attempt = () => {
      const el = find()
      if (el) {
        el.scrollIntoView({behavior: 'smooth'})
        return
      }
      framesLeft -= 1
      if (framesLeft > 0) frameId = requestAnimationFrame(attempt)
    }
    attempt()
    return () => {
      framesLeft = 0
      if (frameId) cancelAnimationFrame(frameId)
    }
  }

  scroll()
  return () => undefined
}

interface Props {
  name?: string
  /** This indicates the margin at the top when scrolling to the component.
   *  Only useful if you have a fixed header, in which case this value
   *  should be the height of the header.
   */
  scrollTopMargin?: number
  as?: React.ElementType
  id?: string
  children: React.ReactNode
  dataId?: string
  /** Extra data-* attributes, e.g. verification hooks for tests and QA */
  dataAttributes?: Record<`data-${string}`, string | undefined>
}

const StyledSection = styled.div<{scrollTopMargin?: number}>`
  scroll-margin-top: ${({scrollTopMargin}) => scrollTopMargin}px;
`

export const ScrollSection: React.FC<Props> = ({
  name,
  children,
  scrollTopMargin,
  as = 'section',
  id,
  dataId,
  dataAttributes
}) => (
  // Spread first so callers can never clobber the scroll/test anchors
  <StyledSection
    {...dataAttributes}
    scrollTopMargin={scrollTopMargin}
    data-scrollname={name}
    as={as}
    id={id}
    data-id={dataId}
  >
    {children}
  </StyledSection>
)
